import React, { useState, useEffect, useRef } from 'react';
import './Services.css';

const tabs = [
  { id: 'consumer', label: 'Consumer Solutions' },
  { id: 'corporate', label: 'Corporate Solutions' },
];

const services = {
  consumer: [
    {
      img: 'https://images.unsplash.com/photo-1604357209793-fca5dca89f97?w=600&q=80',
      title: 'Real-Time Tracking',
      desc: "See your vehicle's exact location on a live map, updated every minute, from anywhere on your phone.",
      badge: 'Live',
    },
    {
      img: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=600&q=80',
      title: 'Trip History',
      desc: 'Full playback of all routes — day, week, or month. Export reports instantly.',
      badge: 'Reports',
    },
    {
      img: 'https://images.unsplash.com/photo-1772461355574-3fcd84c6016b?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      title: 'Secure Parking',
      desc: 'Instant alerts the moment your parked vehicle moves — stop theft before it happens.',
      badge: 'Alert',
    },
    {
      img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
      title: 'Fuel Monitoring',
      desc: 'Track fuel levels in real-time and get alerted to any sudden drops or theft attempts.',
      badge: 'Savings',
    },
    {
      img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
      title: 'Smart Reports',
      desc: 'Detailed analytics on speed, distance, idle time, and driving behaviour — auto-generated.',
      badge: 'Analytics',
    },
    {
      img: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&q=80',
      title: "Today's Track",
      desc: "A complete daily summary of where your vehicle went — departure, stops, and arrival.",
      badge: 'Daily',
    },
    {
      img: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=600&q=80',
      title: 'Driver Feedback',
      desc: 'Score driver behaviour, flag harsh braking or speeding, and drive a safety culture.',
      badge: 'Safety',
    },
    {
      img: 'https://images.unsplash.com/photo-1476900543704-4312b78632f8?w=600&q=80',
      title: 'Current Position',
      desc: 'One-tap precise location sharing with family, office, or logistics teams.',
      badge: 'Share',
    },
  ],
  corporate: [
    {
      img: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=80',
      title: 'Fleet Tracking',
      desc: 'Real-time map view of your entire fleet — 5 vehicles or 5000, managed from one dashboard.',
      badge: 'Core',
    },
    {
      img: 'https://images.unsplash.com/photo-1611284446314-60a58ac0deb9?w=600&q=80',
      title: 'Fuel Management',
      desc: 'Cut fuel costs by up to 30% with live consumption tracking and theft detection alerts.',
      badge: '30% Off',
    },
    {
      img: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&q=80',
      title: 'Vehicle Maintenance',
      desc: 'Auto-schedule maintenance by mileage and engine hours. Never miss a service again.',
      badge: 'Auto',
    },
    {
      img: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=600&q=80',
      title: 'Temperature Control',
      desc: 'Cold chain monitoring with live in-cabin sensors — safeguard pharma, food, and perishables.',
      badge: 'Cold Chain',
    },
    {
      img: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600&q=80',
      title: 'Supply Chain',
      desc: 'End-to-end shipment visibility. Know exactly where every consignment is at every moment.',
      badge: 'E2E',
    },
    {
      img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
      title: 'Load Monitoring',
      desc: 'Detect overloading in real-time. Protect your vehicles and stay fully compliant.',
      badge: 'Compliance',
    },
    {
      img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80',
      title: 'Fleet Reports',
      desc: 'One-click executive dashboards — fuel, mileage, driver scores, and fleet efficiency.',
      badge: 'Insights',
    },
    {
      img: 'https://images.unsplash.com/photo-1587440871875-191322ee64b0?w=600&q=80',
      title: 'Video Monitoring',
      desc: 'In-cabin and external cameras for driver safety, incident recording, and liability proof.',
      badge: 'HD',
    },
  ],
};

const Services = () => {
  const [activeTab, setActiveTab] = useState('consumer');
  const gridRef = useRef(null);

  useEffect(() => {
    const items = gridRef.current?.querySelectorAll('.svc-card') || [];
    items.forEach(el => el.classList.remove('visible'));
    const timeout = setTimeout(() => {
      items.forEach((el, i) => {
        setTimeout(() => el.classList.add('visible'), i * 60);
      });
    }, 20);
    return () => clearTimeout(timeout);
  }, [activeTab]);

  return (
    <section className="services-section section" id="services">
      <div className="container">
        <div className="section-header reveal">
          <span className="section-label">Our Solutions</span>
          <h2>Powerful Tracking Solutions<br />for <em>Every Need</em></h2>
          <div className="divider" />
          <p>From individual vehicle owners to enterprise fleets — we have a solution built exactly for you.</p>
        </div>

        <div className="svc-tabs reveal">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`svc-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="svc-grid" ref={gridRef}>
          {services[activeTab].map((svc, i) => (
            <div key={`${activeTab}-${i}`} className="svc-card">
              <div className="svc-card-img">
                <img src={svc.img} alt={svc.title} loading="lazy" />
              </div>
              <div className="svc-card-body">
                <div className="svc-card-top">
                  <h3 className="svc-title">{svc.title}</h3>
                  {svc.badge && <span className="svc-badge">{svc.badge}</span>}
                </div>
                <p className="svc-desc">{svc.desc}</p>
                <span className="svc-link">
                  Learn more
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="svc-cta reveal">
          <a href="/contact-us" className="btn btn-primary">
            Explore All Solutions
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;